import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["list", "count", "importText", "importPreview", "importBtn", "addInput", "toggle", "importForm", "importDescription", "serviceButtons", "clearBtn"]

  connect() {
    this.memories = []
    this.load()
    this.startPlaceholderRotation()
  }

  disconnect() {
    clearInterval(this._placeholderInterval)
  }

  startPlaceholderRotation() {
    if (!this.hasAddInputTarget) return

    const examples = [
      "I prefer concise, professional writing",
      "I work in healthcare / life sciences",
      "My preferred language is English",
      "I manage a team of 12 people",
      "I write regulatory documents for FDA submissions",
      "I prefer bullet points over long paragraphs",
      "My company name is Acme Corp",
      "I'm based in New York, Eastern timezone",
      "I have a PhD in biochemistry",
      "I prefer a formal, academic tone",
      "My audience is typically C-suite executives",
      "I use APA citation format",
    ]

    let idx = 0
    this.addInputTarget.placeholder = examples[0]
    this._placeholderInterval = setInterval(() => {
      idx = (idx + 1) % examples.length
      this.addInputTarget.placeholder = examples[idx]
    }, 4000)
  }

  async load() {
    try {
      const res = await this.fetch("/memories.json", { method: "GET" })
      await this.checkResponse(res)
      if (res.ok) {
        this.memories = await res.json()
        this.render()
      }
    } catch (e) {
      alert(e.message || "Failed to load memories. Please try again.")
    }
  }

  render() {
    if (!this.hasListTarget) return

    if (this.hasCountTarget) {
      this.countTarget.textContent = this.memories.filter(m => m.active && !m.expired).length
    }

    if (this.memories.length === 0) {
      this.listTarget.innerHTML = `
        <div class="text-muted font-size-14 p-3 text-center">
          No memories yet. Import from another AI service or add one manually.
        </div>
      `
      return
    }

    this.listTarget.innerHTML = ""
    // Sort: pinned first, then by creation order
    const sorted = [...this.memories].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1
      if (!a.pinned && b.pinned) return 1
      return 0
    })
    sorted.forEach(m => {
      const item = document.createElement("div")
      item.className = `memory-item ${m.active && !m.expired ? "" : "memory-inactive"} ${m.pinned ? "memory-pinned" : ""}`
      item.dataset.id = m.id

      const categoryBadge = m.category ? `<span class="memory-category">${this.escapeHtml(m.category)}</span>` : ""
      const expiredBadge = m.expired ? '<span class="memory-category" title="Star this memory to include it in AI conversations again">Expired</span>' : ""
      const sourceBadge = m.source === "auto" ? `<span class="memory-source-auto" title="Captured automatically from your conversations"><i class="fas fa-sparkles mr-1"></i>auto</span>` : ""

      item.innerHTML = `
        <div class="memory-item-content">
          <span class="memory-text">${this.escapeHtml(m.content)}</span>
          ${categoryBadge}${sourceBadge}${expiredBadge}
        </div>
        <div class="memory-item-actions">
          <button type="button" class="memory-action-btn memory-pin-btn" title="${m.pinned ? "Unstar" : "Star (always included in context)"}">
            <span class="fas ${m.pinned ? "fa-star text-color-gold" : "far fa-star text-muted opacity-4"}"></span>
          </button>
          <button type="button" class="memory-action-btn memory-toggle-btn" title="${m.active ? "Disable" : "Enable"}">
            <span class="fas ${m.active ? "fa-toggle-on text-success" : "fa-toggle-off text-muted"}"></span>
          </button>
          <button type="button" class="memory-action-btn memory-delete-btn" title="Delete">
            <span class="fas fa-trash-alt"></span>
          </button>
        </div>
      `

      item.querySelector(".memory-pin-btn").addEventListener("click", () => this.pinMemory(m.id))
      item.querySelector(".memory-toggle-btn").addEventListener("click", () => this.toggleMemory(m.id))
      item.querySelector(".memory-delete-btn").addEventListener("click", () => this.deleteMemory(m.id))
      this.listTarget.appendChild(item)
    })
  }

  async addMemory() {
    if (!this.hasAddInputTarget) return
    const content = this.addInputTarget.value.trim()
    if (!content) return

    try {
      const res = await this.fetch("/memories.json", {
        method: "POST",
        body: JSON.stringify({ content }),
      })
      if (res.ok) {
        const memory = await res.json()
        this.memories.push(memory)
        this.addInputTarget.value = ""
        this.render()
      } else {
        const data = await res.json().catch(() => ({}))
        alert(data.error || data.errors?.join(", ") || "Failed to add memory.")
      }
    } catch (e) {
      alert(e.message || "Failed to add memories. Please try again.")
    }
  }

  async toggleMemory(id) {
    try {
      const res = await this.fetch(`/memories/${id}/toggle.json`, { method: "PATCH" })
      await this.checkResponse(res)
      if (res.ok) {
        const updated = await res.json()
        const idx = this.memories.findIndex(m => m.id === id)
        if (idx >= 0) this.memories[idx] = updated
        this.render()
      }
    } catch (e) {
      alert(e.message || "Failed to toggle memories. Please try again.")
    }
  }

  async pinMemory(id) {
    try {
      const res = await this.fetch(`/memories/${id}/pin.json`, { method: "PATCH" })
      await this.checkResponse(res)
      if (res.ok) {
        const updated = await res.json()
        const idx = this.memories.findIndex(m => m.id === id)
        if (idx >= 0) this.memories[idx] = updated
        this.render()
      }
    } catch (e) {
      alert(e.message || "Failed to pin memories. Please try again.")
    }
  }

  async deleteMemory(id) {
    if (!confirm("Delete this memory?")) return

    try {
      const res = await this.fetch(`/memories/${id}.json`, { method: "DELETE" })
      await this.checkResponse(res)
      this.memories = this.memories.filter(m => m.id !== id)
      this.render()
    } catch (e) {
      alert(e.message || "Failed to delete memories. Please try again.")
    }
  }

  selectService(e) {
    const service = e.currentTarget.dataset.service
    if (!this.hasImportFormTarget || !this.hasImportDescriptionTarget) return

    const descriptions = {
      "OpenAI": "In <strong>ChatGPT</strong>, go to <strong>Settings &gt; Personalization &gt; Manage Memory</strong> to see all your saved memories. You can also start a new chat and ask ChatGPT the prompt below. Copy the response and paste it here.",
      "Anthropic": "In <strong>Claude</strong>, go to <strong>Settings &gt; Memory</strong> to view what Claude remembers, or start a new chat and ask Claude the prompt below. Copy the response and paste it here.",
      "Google": "In <strong>Gemini</strong>, go to <strong>Settings &gt; Extensions &gt; Saved Info</strong> to view what Gemini remembers, or start a new chat and ask it the prompt below. Copy the response and paste it here.",
      "xAI": "Start a new conversation in <strong>Grok</strong> and ask it the prompt below. Copy the response and paste it here.",
      "Other": "Open your AI assistant, start a new conversation, and ask it the prompt below. Copy the response and paste it here.",
    }

    this.importDescriptionTarget.innerHTML = descriptions[service] || descriptions["Other"]
    this.importFormTarget.classList.remove("d-none")
    if (this.hasServiceButtonsTarget) this.serviceButtonsTarget.classList.add("d-none")
  }

  cancelImport() {
    if (this.hasImportFormTarget) this.importFormTarget.classList.add("d-none")
    if (this.hasServiceButtonsTarget) this.serviceButtonsTarget.classList.remove("d-none")
    if (this.hasImportTextTarget) this.importTextTarget.value = ""
    if (this.hasImportPreviewTarget) this.importPreviewTarget.classList.add("d-none")
  }

  copyPrompt() {
    const prompt = "Please list everything you know or remember about me. Include my preferences, background, work context, writing style, and any facts you've learned about me from our conversations. Format each item as a separate bullet point."
    navigator.clipboard.writeText(prompt).then(() => {
      const btn = this.element.querySelector(".copy-prompt-btn")
      if (btn) {
        const original = btn.innerHTML
        btn.innerHTML = '<span class="fas fa-check mr-1"></span>Copied!'
        setTimeout(() => { btn.innerHTML = original }, 2000)
      }
    })
  }

  async parseImport() {
    if (!this.hasImportTextTarget || !this.hasImportBtnTarget) return
    const text = this.importTextTarget.value.trim()
    if (!text) return

    this.importBtnTarget.disabled = true
    this.importBtnTarget.innerHTML = '<span class="fas fa-spinner fa-spin mr-1"></span>Importing...'

    // Show progress indicator below the button
    const progressEl = document.createElement("div")
    progressEl.className = "import-progress mt-3"
    progressEl.innerHTML = `
      <div class="d-flex align-items-center mb-2">
        <span class="fas fa-brain text-color-highlight mr-2"></span>
        <span class="font-size-14 font-weight-bold import-progress-status" style="min-width: 280px;">Analyzing your memories...</span>
      </div>
      <div class="import-progress-bar-container">
        <div class="import-progress-bar"></div>
      </div>
      <p class="font-size-13 text-muted mt-2 mb-0">This may take up to a minute for large imports.</p>
    `
    this.importBtnTarget.parentNode.insertBefore(progressEl, this.importBtnTarget.nextSibling)

    // Animate progress stages
    const statusEl = progressEl.querySelector(".import-progress-status")
    const barEl = progressEl.querySelector(".import-progress-bar")
    const stages = [
      { text: "Analyzing your memories...", width: "10%", delay: 0 },
      { text: "Extracting key facts and preferences...", width: "25%", delay: 3000 },
      { text: "Identifying important items to star...", width: "40%", delay: 8000 },
      { text: "Organizing by category...", width: "55%", delay: 14000 },
      { text: "Checking for duplicates...", width: "65%", delay: 20000 },
      { text: "Preparing your memory list...", width: "75%", delay: 28000 },
      { text: "Finalizing import...", width: "82%", delay: 36000 },
      { text: "Just a few more seconds...", width: "88%", delay: 44000 },
      { text: "Wrapping up...", width: "93%", delay: 52000 },
      { text: "Almost there...", width: "96%", delay: 60000 },
    ]
    const timeouts = []
    stages.forEach(stage => {
      timeouts.push(setTimeout(() => {
        if (statusEl) statusEl.textContent = stage.text
        if (barEl) barEl.style.width = stage.width
      }, stage.delay))
    })

    try {
      const res = await this.fetch("/memories/import.json", {
        method: "POST",
        body: JSON.stringify({ text }),
      })
      const data = await res.json()

      if (!res.ok) {
        alert(data.error || "Import failed. Please try again.")
      } else if (data.items && data.items.length > 0) {
        this.showImportPreview(data.items)
      } else {
        alert("No memories could be parsed from the text. Try a different format.")
      }
    } catch (e) {
      alert("Failed to parse memories. Please try again.")
    } finally {
      timeouts.forEach(t => clearTimeout(t))
      progressEl.remove()
      this.importBtnTarget.disabled = false
      this.importBtnTarget.innerHTML = '<span class="fas fa-file-import mr-1"></span>Import'
    }
  }

  showImportPreview(items) {
    if (!this.hasImportPreviewTarget) return

    this.importPreviewTarget.innerHTML = ""
    this.importPreviewTarget.classList.remove("d-none")
    this._pendingItems = items

    const heading = document.createElement("div")
    heading.className = "font-size-16 font-weight-bold mb-3"
    heading.innerHTML = '<span class="fas fa-check-circle mr-2 text-color-highlight"></span>Confirm memories to be added:'
    this.importPreviewTarget.appendChild(heading)

    items.forEach((item, i) => {
      const row = document.createElement("div")
      row.className = "import-preview-item"
      row.dataset.index = i

      const starIcon = item.starred ? '<span class="fas fa-star text-color-gold mr-1" title="Starred"></span>' : ''

      row.innerHTML = `
        <div class="d-flex align-items-start">
          <input type="checkbox" checked class="import-checkbox mt-1 mr-2">
          <div class="flex-fill">
            <div class="font-size-14">${starIcon}${this.escapeHtml(item.content)}</div>
            <span class="memory-category">${this.escapeHtml(item.category || "fact")}</span>
          </div>
        </div>
      `
      this.importPreviewTarget.appendChild(row)
    })

    const actions = document.createElement("div")
    actions.className = "d-flex justify-content-end gap-2 mt-4"
    actions.innerHTML = `
      <button type="button" class="btn btn-md btn-2 px-4 import-cancel-btn">Cancel</button>
      <button type="button" class="btn btn-md btn-5 px-4 import-confirm-btn">
        <span class="fas fa-check mr-1"></span>Save ${items.length} Memories
      </button>
    `
    actions.querySelector(".import-cancel-btn").addEventListener("click", () => {
      this.importPreviewTarget.classList.add("d-none")
      this._pendingItems = null
    })
    actions.querySelector(".import-confirm-btn").addEventListener("click", () => this.confirmImport())
    this.importPreviewTarget.appendChild(actions)
  }

  async confirmImport() {
    if (!this._pendingItems || this._savingImport) return

    const checkboxes = this.importPreviewTarget.querySelectorAll(".import-checkbox")
    const selected = this._pendingItems.filter((_, i) => checkboxes[i]?.checked)
    if (selected.length === 0) return

    this._savingImport = true
    const button = this.importPreviewTarget.querySelector(".import-confirm-btn")
    if (button) button.disabled = true

    try {
      const res = await this.fetch("/memories/bulk_create.json", {
        method: "POST",
        body: JSON.stringify({ items: selected }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to save memories.")
      if (data.count === 0) {
        alert("No memories were saved. Items may be duplicates, invalid, or over the memory limit.")
        return
      }
      if (data.skipped > 0) {
        alert(`${data.count} memories saved; ${data.skipped} skipped (duplicates, invalid items, or memory limit).`)
        const unsaved = selected.filter((_, index) => data.skipped_indices?.includes(index))
        this.showImportPreview(unsaved.length ? unsaved : selected)
        await this.load()
        return
      }

      this.importPreviewTarget.classList.add("d-none")
      this.importTextTarget.value = ""
      this._pendingItems = null
      await this.load() // Reload only after the server confirms persistence
    } catch (e) {
      alert(e.message || "Failed to save memories.")
    } finally {
      this._savingImport = false
      if (button) button.disabled = false
    }
  }

  async clearAll() {
    if (!confirm("Are you sure you want to delete all your memories? This cannot be undone.")) return

    try {
      const res = await this.fetch("/memories/destroy_all.json", { method: "DELETE" })
      await this.checkResponse(res)
      this.memories = []
      this.render()
    } catch (e) {
      alert(e.message || "Failed to clear all memories. Please try again.")
    }
  }

  addOnEnter(e) {
    if (e.key === "Enter") {
      e.preventDefault()
      this.addMemory()
    }
  }

  async checkResponse(res) {
    if (res.ok) return
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || data.errors?.join(", ") || "The change could not be saved. Please try again.")
  }

  fetch(path, options = {}) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    return window.fetch(path, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": csrfToken,
        ...options.headers,
      },
      credentials: "same-origin",
    })
  }

  escapeHtml(text) {
    return (text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }
}
